import { useId, type CSSProperties, type ReactNode } from 'react'
import { motion } from 'framer-motion'
import { cn } from '../lib/cn'

/**
 * The marks that are drawn here rather than taken from the library, on the
 * 24-unit grid the Lucide set shares, so they sit beside it unnoticed.
 *
 * One reason, and only one: rounding. Lucide is a stroke set with its own
 * corner weights, and its square is a box — two pixels of radius on an
 * eighteen-pixel field. As the glyph of a transport control that is the
 * hardest corner on screen, and a control meaning "hold" should be the
 * softest. The mark below keeps the metaphor and rounds the corners off.
 */
export function StopMark({
  size = '100%',
  strokeWidth = 1.2,
  className,
  style,
}: {
  size?: string | number
  strokeWidth?: number
  className?: string
  style?: CSSProperties
}): ReactNode {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={strokeWidth}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
      className={cn('wf-icon shrink-0', className)}
      style={{ width: size, height: size, ...style }}
    >
      <rect x="5.5" y="5.5" width="13" height="13" rx="4.5" />
    </svg>
  )
}

/**
 * The sidebar toggle, drawn the way a Mac window draws it: a soft, deeply
 * rounded frame and the pane's divider inside it. The mark is alive to the
 * state it controls — the divider always stands a third of the way in, so
 * the shape reads as a sidebar either way; open, the pane is filled behind
 * it; closed, the pane fades to a ghost and the divider turns dashed — a
 * sidebar put away, not gone.
 */
export function SidebarToggleMark({
  open,
  size = 18,
  className,
}: {
  open: boolean
  size?: string | number
  className?: string
}): ReactNode {
  const clipId = useId()
  const divider = 9.25
  const spring = { type: 'spring', stiffness: 420, damping: 34, mass: 0.8 } as const
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.3}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
      className={cn('wf-icon shrink-0', className)}
      style={{ width: size, height: size }}
    >
      <defs>
        <clipPath id={clipId}>
          <rect x="3" y="4.5" width="18" height="15" rx="4.75" />
        </clipPath>
      </defs>
      <g clipPath={`url(#${clipId})`}>
        <motion.rect
          x="3"
          y="4.5"
          height="15"
          fill="currentColor"
          stroke="none"
          initial={false}
          animate={{ width: divider - 3, opacity: open ? 0.5 : 0.15 }}
          transition={spring}
        />
      </g>
      <rect x="3" y="4.5" width="18" height="15" rx="4.75" />
      <line x1={divider} x2={divider} y1="4.5" y2="19.5" strokeDasharray={open ? undefined : '2 2'} strokeLinecap={open ? 'round' : 'butt'} />
    </svg>
  )
}
